<script lang="ts">
  import { Triajes, Signos, type Triaje } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import { session } from '../lib/auth.svelte';
  import HelpHint from './HelpHint.svelte';

  let pacienteId = $state('');
  let items = $state<Triaje[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  const NIVELES: { value: Triaje['nivel']; label: string; color: string }[] = [
    { value: 'I', label: 'I · Rojo (inmediato)', color: '#dc2626' },
    { value: 'II', label: 'II · Naranja (muy urgente)', color: '#ea580c' },
    { value: 'III', label: 'III · Amarillo (urgente)', color: '#d97706' },
    { value: 'IV', label: 'IV · Verde (estándar)', color: '#16a34a' },
    { value: 'V', label: 'V · Azul (no urgente)', color: '#2563eb' },
  ];

  let nuevo = $state<Triaje>({
    pacienteId: '', motivo: '', nivel: 'IV', ta: '', fc: null, fr: null,
    temp: null, satO2: null, enfermero: session.value?.usuario ?? '', notas: '',
  });
  let notasEnf = $state('');
  let insulinaTipo = $state('');
  let insulinaDosis = $state('');
  let liquidoIng = $state('');
  let liquidoEg = $state('');
  let diuresis = $state('');
  let dictando = $state(false);
  let recDictado: any = null;

  const SpeechRecognition = typeof window !== 'undefined'
    ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
    : null;

  function toggleDictado() {
    if (!SpeechRecognition) { error = 'El dictado por voz no está disponible en este navegador.'; return; }
    if (dictando && recDictado) { recDictado.stop(); dictando = false; return; }
    const rec = new SpeechRecognition();
    rec.lang = 'es-GT';
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (ev: any) => {
      const t = Array.from(ev.results as any[]).map((r: any) => r[0].transcript).join(' ');
      notasEnf = (notasEnf ? notasEnf + ' ' : '') + t;
    };
    rec.onend = () => { dictando = false; };
    recDictado = rec;
    rec.start();
    dictando = true;
  }

  const alertaVital = $derived.by(() => {
    const avisos: string[] = [];
    const fc = Number(nuevo.fc);
    const sat = Number(nuevo.satO2);
    const t = Number(nuevo.temp);
    if (fc && (fc < 50 || fc > 120)) avisos.push(`FC ${fc} fuera de rango (50–120)`);
    if (sat && sat < 92) avisos.push(`SatO₂ ${sat}% crítica (< 92)`);
    if (t && t >= 38.5) avisos.push(`Fiebre ${t} °C`);
    const sys = Number(String(nuevo.ta ?? '').split('/')[0]);
    if (sys && sys >= 180) avisos.push(`TA ${nuevo.ta} hipertensiva`);
    return avisos;
  });

  async function cargar() {
    if (!pacienteId) { items = []; return; }
    loading = true; error = null;
    try { items = await Triajes.porPaciente(pacienteId); }
    catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  function onPick(id: string) { pacienteId = id; void cargar(); }

  async function registrar(e: SubmitEvent) {
    e.preventDefault();
    if (!pacienteId) { error = 'Selecciona un paciente.'; return; }
    if (!nuevo.motivo?.trim()) { error = 'Escribe el motivo del triaje.'; return; }
    error = null; info = null;
    const extra = [
      notasEnf.trim() ? `Notas de enfermería: ${notasEnf.trim()}` : '',
      insulinaTipo || insulinaDosis ? `Insulina: ${insulinaTipo || '—'} ${insulinaDosis || ''}`.trim() : '',
      liquidoIng || liquidoEg || diuresis
        ? `Balance hídrico: Ing ${liquidoIng || '0'} / Eg ${liquidoEg || '0'} / Diuresis ${diuresis || '0'}`
        : '',
    ].filter(Boolean).join('\n');
    try {
      await Triajes.crear({
        ...nuevo,
        pacienteId,
        notas: extra || nuevo.notas,
        fc: nuevo.fc != null ? Number(nuevo.fc) : null,
        fr: nuevo.fr != null ? Number(nuevo.fr) : null,
        temp: nuevo.temp != null ? Number(nuevo.temp) : null,
        satO2: nuevo.satO2 != null ? Number(nuevo.satO2) : null,
      });
      // Copiar signos vitales del triaje al expediente clínico.
      await Signos.registrar({
        pacienteId,
        ta: nuevo.ta || null,
        fc: nuevo.fc != null ? Number(nuevo.fc) : null,
        fr: nuevo.fr != null ? Number(nuevo.fr) : null,
        temp: nuevo.temp != null ? Number(nuevo.temp) : null,
        satO2: nuevo.satO2 != null ? Number(nuevo.satO2) : null,
      }).catch(() => {});
      info = alertaVital.length
        ? `Triaje registrado. ⚠ ${alertaVital.join(' · ')}`
        : 'Triaje registrado. Signos vitales copiados al expediente clínico.';
      nuevo = { pacienteId: '', motivo: '', nivel: 'IV', ta: '', fc: null, fr: null, temp: null, satO2: null, enfermero: session.value?.usuario ?? '', notas: '' };
      notasEnf = ''; insulinaTipo = ''; insulinaDosis = ''; liquidoIng = ''; liquidoEg = ''; diuresis = '';
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  function nivelColor(n?: Triaje['nivel']) {
    return NIVELES.find(nv => nv.value === n)?.color ?? '#6b7280';
  }

  function fmt(iso?: string) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('es-GT'); } catch { return iso; }
  }
</script>

<section class="view">
  <header class="view-header">
    <div>
      <h2>Triaje de enfermería</h2>
      <p class="muted">Valoración inicial, notas de turno, insulina y balance hídrico</p>
      <HelpHint title="Guía rápida · Triaje">
        <p>Las notas de enfermería (con dictado) forman parte legal del expediente. El balance hídrico calcula diuresis. Signos fuera de rango generan alerta de prioridad.</p>
      </HelpHint>
    </div>
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <div class="picker-row">
    <PacientePicker bind:value={pacienteId} onPick={onPick} placeholder="Buscar paciente…" />
    <button onclick={cargar} disabled={loading || !pacienteId}>{loading ? '…' : 'Recargar'}</button>
  </div>

  {#if pacienteId}
    <form onsubmit={registrar} class="panel">
      <h3>Registrar triaje</h3>
      <div class="grid">
        <label class="field full"><span>Motivo de consulta *</span>
          <input bind:value={nuevo.motivo} placeholder="Ej. dolor abdominal, fiebre, trauma…" required />
        </label>
        <label class="field"><span>Nivel de prioridad</span>
          <select bind:value={nuevo.nivel}>
            {#each NIVELES as n (n.value)}<option value={n.value}>{n.label}</option>{/each}
          </select>
        </label>
        <label class="field"><span>Enfermero/a</span>
          <input bind:value={nuevo.enfermero} placeholder="Nombre" />
        </label>
        <label class="field"><span>TA</span><input bind:value={nuevo.ta} placeholder="120/80" /></label>
        <label class="field"><span>FC</span><input type="number" min="0" bind:value={nuevo.fc} placeholder="lpm" /></label>
        <label class="field"><span>FR</span><input type="number" min="0" bind:value={nuevo.fr} placeholder="rpm" /></label>
        <label class="field"><span>Temp °C</span><input type="number" step="0.1" bind:value={nuevo.temp} placeholder="36.5" /></label>
        <label class="field"><span>SatO₂ %</span><input type="number" min="0" max="100" bind:value={nuevo.satO2} placeholder="98" /></label>
        <label class="field full"><span>Notas de enfermería (evolución de turno)</span>
          <textarea rows="3" bind:value={notasEnf} placeholder="Dictado o texto: evolución, herida, dolor, dieta…"></textarea>
          {#if SpeechRecognition}
            <button type="button" class="mic" class:on={dictando} onclick={toggleDictado}>{dictando ? '⏹ Detener dictado' : '🎤 Dictado por voz'}</button>
          {/if}
        </label>
        <label class="field"><span>Insulina · tipo</span>
          <select bind:value={insulinaTipo}>
            <option value="">— No aplica —</option>
            <option>Rápida / Regular</option>
            <option>NPH</option>
            <option>Glargina</option>
            <option>Lispro / Aspart</option>
            <option>Mixta 70/30</option>
          </select>
        </label>
        <label class="field"><span>Insulina · dosis</span><input bind:value={insulinaDosis} placeholder="Ej. 10 UI" /></label>
        <label class="field"><span>Ingesta líquidos (ml)</span><input type="number" min="0" bind:value={liquidoIng} placeholder="1500" /></label>
        <label class="field"><span>Egresos líquidos (ml)</span><input type="number" min="0" bind:value={liquidoEg} placeholder="1200" /></label>
        <label class="field"><span>Diuresis (ml)</span><input type="number" min="0" bind:value={diuresis} placeholder="800" /></label>
      </div>
      {#if alertaVital.length}
        <p class="alerta-vital">⚠ {alertaVital.join(' · ')} — considere subir el nivel de prioridad.</p>
      {/if}
      <button type="submit" class="primary">Registrar triaje</button>
    </form>

    <h3>Historial de triaje ({items.length})</h3>
    <table>
      <thead><tr><th>Fecha</th><th>Motivo</th><th>Nivel</th><th>TA</th><th>FC</th><th>FR</th><th>T°</th><th>SatO₂</th><th>Enfermero/a</th><th>Notas</th></tr></thead>
      <tbody>
        {#each items as t (t.id)}
          <tr>
            <td>{fmt(t.fecha)}</td>
            <td>{t.motivo ?? '—'}</td>
            <td><span class="nivel" style="background:{nivelColor(t.nivel)}">{t.nivel}</span></td>
            <td>{t.ta ?? '—'}</td>
            <td>{t.fc ?? '—'}</td>
            <td>{t.fr ?? '—'}</td>
            <td>{t.temp ?? '—'}</td>
            <td>{t.satO2 ?? '—'}</td>
            <td>{t.enfermero ?? '—'}</td>
            <td class="notas">{t.notas ?? '—'}</td>
          </tr>
        {:else}
          <tr><td colspan="10" class="empty">Sin triajes registrados.</td></tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <p class="hint">Selecciona un paciente para registrar su triaje.</p>
  {/if}
</section>

<style>
  .view { padding: 1rem; }
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  .picker-row { display: flex; gap: .5rem; align-items: center; margin-bottom: 1rem; }
  .picker-row :global(.picker) { flex: 1; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin-bottom: 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .panel h3 { margin: 0 0 .75rem; color: #166534; }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: .6rem; margin-bottom: .8rem; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field.full { grid-column: span 2; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  input, select { padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; background: #fff; color: #111827; }
  input:focus, select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  button { padding: .5rem .9rem; border: 0; border-radius: 8px; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; font: inherit; font-weight: 600; cursor: pointer; box-shadow: 0 4px 14px rgba(22,163,74,0.3); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  h3 { margin: 1rem 0 .5rem; font-size: 1rem; color: #166534; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .5rem .6rem; background: #f0fdf4; }
  td { border-top: 1px solid #f3f4f6; padding: .45rem .6rem; font-size: .88rem; color: #111827; }
  .nivel { color: #fff; padding: .1rem .5rem; border-radius: 999px; font-weight: 700; font-size: .75rem; }
  .empty { text-align: center; color: #9ca3af; font-style: italic; }
  .hint { color: #6b7280; font-style: italic; margin-top: 1rem; }
  .err { color: #dc2626; } .ok { color: #166534; font-weight: 600; }
  textarea { padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; min-height: 4rem; }
  .mic { margin-top: .35rem; background: #fff; color: #166534; border: 1px solid #bbf7d0; box-shadow: none; }
  .mic.on { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
  .alerta-vital { background: #fef3c7; color: #92400e; padding: .55rem .75rem; border-radius: 8px; font-weight: 600; }
  td.notas { max-width: 220px; font-size: .8rem; white-space: pre-wrap; }
  @media (max-width: 700px) { .grid { grid-template-columns: 1fr 1fr; } }
</style>
